import React from "react";
import { RefreshCw } from "lucide-react";
import { format, formatDistanceStrict } from "date-fns";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { ReviewServices, type ReviewOverviewSnapshot } from "@/services/bedrock";

const OVERDUE_GRACE_MS = 60_000;
const CLOCK_TICK_MS = 1_000;
const OVERDUE_POLL_MS = 30_000;

function isUpdateOverdue(autoUpdateAt: string | null | undefined, nowMs: number): boolean {
  if (!autoUpdateAt) return false;
  return nowMs - new Date(autoUpdateAt).getTime() > OVERDUE_GRACE_MS;
}

function isPersistedSnapshot(snapshot?: ReviewOverviewSnapshot | null): snapshot is ReviewOverviewSnapshot {
  return snapshot != null && snapshot.id >= 0;
}

function snapshotSyncChanged(before: ReviewOverviewSnapshot, after: ReviewOverviewSnapshot): boolean {
  return before.updatedAt !== after.updatedAt || before.autoUpdateAt !== after.autoUpdateAt;
}

export function OverviewSnapshotSync({
  snapshot,
  isRefreshing,
  onRefresh,
  onSnapshotSynced,
}: {
  snapshot?: ReviewOverviewSnapshot | null;
  isRefreshing: boolean;
  onRefresh: () => void;
  onSnapshotSynced?: (snapshot: ReviewOverviewSnapshot) => void | Promise<void>;
}) {
  const updatedAt = snapshot?.updatedAt ?? null;
  const autoUpdateAt = snapshot?.autoUpdateAt ?? null;
  const snapshotId = isPersistedSnapshot(snapshot) ? snapshot.id : null;
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  const [overdueConfirmed, setOverdueConfirmed] = React.useState(false);
  const onSnapshotSyncedRef = React.useRef(onSnapshotSynced);
  onSnapshotSyncedRef.current = onSnapshotSynced;
  const snapshotRef = React.useRef(snapshot);
  snapshotRef.current = snapshot;

  React.useEffect(() => {
    setNowMs(Date.now());
    if (!updatedAt && !autoUpdateAt) return;

    const id = window.setInterval(() => {
      setNowMs(Date.now());
    }, CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, [updatedAt, autoUpdateAt]);

  React.useEffect(() => {
    setOverdueConfirmed(false);
  }, [snapshotId, autoUpdateAt]);

  React.useEffect(() => {
    if (snapshotId == null || !autoUpdateAt) return;
    if (!isUpdateOverdue(autoUpdateAt, Date.now())) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const latest = await ReviewServices.getReviewSnapshot(snapshotId);
        if (cancelled) return;
        const stillOverdue = isUpdateOverdue(latest.autoUpdateAt, Date.now());
        setOverdueConfirmed(stillOverdue);
        const current = snapshotRef.current;
        if (current && snapshotSyncChanged(current, latest)) {
          await onSnapshotSyncedRef.current?.(latest);
        }
      } catch {
        if (!cancelled) setOverdueConfirmed(true);
      }
    };

    void poll();
    const id = window.setInterval(() => {
      void poll();
    }, OVERDUE_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [snapshotId, autoUpdateAt]);

  if (!isPersistedSnapshot(snapshot)) return null;

  const hasPendingUpdate = !!autoUpdateAt;
  const isOverdue = overdueConfirmed && isUpdateOverdue(autoUpdateAt, nowMs);
  const lastLabel = updatedAt
    ? `Last updated ${formatDistanceStrict(new Date(updatedAt), new Date(nowMs), { addSuffix: true })}`
    : "Not updated yet";
  const nextLabel = !autoUpdateAt
    ? "No auto update scheduled"
    : isOverdue
    ? "Auto update is taking longer than usual. Click refresh, or wait — it'll update on its own soon."
    : `Auto update ~${format(new Date(autoUpdateAt), "MMM d, yyyy h:mm a")}`;

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => {
              if (!isRefreshing) onRefresh();
            }}
            aria-label={isRefreshing ? "Updating overview" : "Update overview now"}
            className={cn(
              "hover:text-foreground",
              hasPendingUpdate ? "text-amber-500 hover:text-amber-600" : "text-muted-foreground"
            )}
          >
            <RefreshCw className={cn("size-3.5", isRefreshing && "animate-spin")} />
          </Button>
        }
      />
      <Tooltip.Content className="font-sans max-w-64">
        <div className="flex flex-col gap-0.5">
          <span>{lastLabel}</span>
          <span className={hasPendingUpdate ? "text-amber-500" : "text-background/70"}>{nextLabel}</span>
        </div>
      </Tooltip.Content>
    </Tooltip>
  );
}
